import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { nextMobileSort, sortMobileRows, type MobileSortState } from "./mobileSorting";
import { exportVisibleColumns } from "./tableExcel";
import { desktopMetricHelp } from "./desktopInformation";
import { hourlyExportKeys, hourlyLegacyKeys, hourlySalesColumns, hourlySalesCopy, hourlySalesDisplay, type HourlyDisplayRow, type HourlySourceRow } from "./hourlySalesPresentation";
import "./storeOverview.css";
import "./hourlySales.css";

type Props = {
  rows: HourlyDisplayRow[];
  totalRow: HourlyDisplayRow;
  sourceRows: HourlySourceRow[];
  language: string;
  hiddenColumns: Set<string>;
  ariaLabel: string;
  currentSort: () => MobileSortState;
  parseNumber: (value: unknown) => number | null;
  onViewChange: (sort: MobileSortState, rowIds: string[]) => void;
};

// This view is used only by Saatlik Satış. Shared tables and Excel builders stay unchanged.
export default function HourlySalesTable(props: Props) {
  const [sort, setSort] = useState(props.currentSort);
  const c = hourlySalesCopy(props.language);
  const available = hourlySalesColumns(props.language);
  const visible = available.filter(column => !props.hiddenColumns.has(column.key));
  const columns = visible.length ? visible : available.slice(0, 1);
  const width = columns.reduce((sum, column) => sum + column.width, 0);
  const prepared = useMemo(() => hourlySalesDisplay(props.rows, props.sourceRows, props.totalRow), [props.rows, props.sourceRows, props.totalRow]);
  const sorted = useMemo(() => sortMobileRows(prepared.rows, sort, (row, key) => key === "label"
    ? String(row.__row_id ?? row.label ?? "") : props.parseNumber(row["__sort_" + key] ?? row[key])), [prepared.rows, sort, props.parseNumber]);
  const rowSignature = JSON.stringify(sorted.map(row => String(row.__row_id ?? row.label)));
  const exportSignature = JSON.stringify(hourlyExportKeys(props.hiddenColumns));
  useEffect(() => {
    exportVisibleColumns.set(props.ariaLabel, JSON.parse(exportSignature));
    return () => { exportVisibleColumns.delete(props.ariaLabel); };
  }, [props.ariaLabel, exportSignature]);
  useEffect(() => {
    // The screen-only cumulative field must not become an Excel sort/column.
    props.onViewChange(sort && hourlyLegacyKeys.includes(sort.key) ? sort : null, JSON.parse(rowSignature));
  }, [sort, rowSignature, props.onViewChange]);
  const renderRow = (row: HourlyDisplayRow, total: boolean) => <tr key={total ? "__total" : String(row.__row_id ?? row.label)}
    className={total ? "store-overview-total" : row.__hourly_peak ? "hourly-sales-peak" : undefined}>
    {columns.map(column => <td key={column.key} data-column={column.key} className={column.key === "label" ? "store-overview-label" : undefined}>{String(row[column.key] ?? "—")}</td>)}
  </tr>;
  return <div className="store-overview-view is-fit hourly-sales-table">
    <div className="store-overview-scroll" role="region" aria-label={props.ariaLabel} tabIndex={0}>
      <table className="store-overview-table" style={{ minWidth: width }}>
        <caption className="store-overview-sr-only">{props.ariaLabel}</caption>
        <colgroup>{columns.map(column => <col key={column.key} style={{ width: column.width / width * 100 + "%" }} />)}</colgroup>
        <thead><tr className="store-overview-headings">{columns.map(column => <th key={column.key} scope="col" data-column={column.key}
          className={column.key === "label" ? "store-overview-label" : undefined}
          aria-sort={sort?.key === column.key ? sort.direction === "asc" ? "ascending" : "descending" : "none"}>
          <button type="button" data-sort-column={column.key} aria-label={column.label + " · " + c.sort}
            title={desktopMetricHelp(column.key, props.language)}
            onClick={() => setSort(nextMobileSort(sort, column.key))}>
            {sort?.key === column.key ? sort.direction === "asc" ? <ArrowUp size={12} aria-hidden /> : <ArrowDown size={12} aria-hidden /> : null}
            <span>{column.key === "cumulativeShare" && props.language === "tr" ? <>Toplanan Ciro<br />Payı %</> : column.label}</span>
          </button>
        </th>)}</tr></thead>
        <tbody>{renderRow(prepared.totalRow, true)}{sorted.map(row => renderRow(row, false))}
          {!sorted.length ? <tr><td colSpan={columns.length} className="hourly-sales-empty">{c.empty}</td></tr> : null}
        </tbody>
      </table>
    </div>
  </div>;
}
